import { html, LitElement, nothing } from 'lit';
import { live } from 'lit/directives/live.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

export class WishlistToolbar extends LitElement {
  static get properties() {
    return {
      selectedProducts: { type: Object, attribute: 'selected-products' }, // Map<originalId, currentSiblingId>
      currentBoard: { type: Object, attribute: 'current-board' },
      loading: { type: Boolean, state: true },
      loading_add: { type: Boolean, state: true },
      loading_move: { type: Boolean, state: true },
      loading_delete: { type: Boolean, state: true },
      selectedProductsCount: { type: Number },
    };
  }

  createRenderRoot() {
    return this;
  }

  constructor() {
    super();
    this.selectedProducts = new Map();
    this.currentBoard = null;
    this.loading = false;
    this.selectedProductsCount = 0;
  }

  updated(changedProperties) {
    super.updated?.(changedProperties);

    if (changedProperties.has('selectedProducts')) {
      this.requestUpdate();
    }
  }

  async _handleAddToCart() {
    this.requestUpdate();
    this.loading = true;
    this.loading_add = true;
    this.busy = true;
    try {
      const wishlistData = await window.wishlist.retrieve();
      const skipped = [];
      const updates = {};
      const successfulOriginalIds = new Set();

      if (!wishlistData) {
        throw new Error('Failed to retrieve wishlist');
      }

      for (const [originalId, productId] of this.selectedProducts) {
        let variantId = wishlistData.productData[productId]?.selectedVariantId;

        if (!variantId) {
          const productElement = document.querySelector(`[selected-product-id="${productId}"]`);

          if (!productElement) {
            skipped.push([originalId, productId]);
            continue;
          }

          variantId = productElement.getAttribute('selected-variant-id');
          if (!variantId) {
            skipped.push([originalId, productId]);
            continue;
          }
        }

        const existingItem = await window.theme.cart.items.find((item) => item.id === parseInt(variantId));
        updates[variantId] = existingItem
          ? existingItem.quantity + 1
          : 1;

        successfulOriginalIds.add(originalId);
      }

      const totalUpdates = new Set(Object.keys(updates)).size;

      const totalLength = new Set(Array.from(this.selectedProducts.values())).size;

      if (totalUpdates !== totalLength || skipped.length > 0) {
        const addModalEvent = new CustomEvent('modal-wishlist-add-notification:open', {
          detail: {
            addedAmount: totalUpdates,
            totalAmount: totalLength,
          },
          bubbles: true,
          composed: true,
        });

        document.dispatchEvent(addModalEvent);
      }

      await window.cartUtils.updateItems(updates);
      const addedToCartEvent = new CustomEvent('wishlist-toolbar:added-to-cart', {
        detail: {
          addedProducts: Array.from(successfulOriginalIds),
        },
        bubbles: true,
        composed: true,
      });

      document.dispatchEvent(addedToCartEvent);

      if (totalUpdates === totalLength) {
        await window.drawerFunctions.toggleDrawer('drawer_sidecart', true);
      }

      window.dispatchEvent(
        new CustomEvent('analytics:gtag', {
          detail: {
            event: 'wishlist_add_all',
            params: {
              total_products: Number(totalLength),
              total_added: Number(totalUpdates),
            },
          },
        })
      );
    } finally {
      this.loading = false;
      this.loading_add = false;
      this.busy = false;
    }
  }

  _handleMoveToBoard() {
    const arrayFromKeys = Array.from(this.selectedProducts.keys());
    const items = arrayFromKeys.map((originalId) => {
      const obj = {
        productId: String(originalId),
      };
      return obj;
    });

    const open = new CustomEvent('modal-wishlist-move-to-board:open', {
      detail: {
        items,
        currentBoard: this.currentBoard,
      },
      bubbles: true,
      composed: true,
    });

    document.dispatchEvent(open);
  }

  async _handleDelete() {
    this.loading = true;
    this.loading_delete = true;
    this.busy = true;

    try {
      const itemsToDelete = new Map(this.selectedProducts);

      const productIdsToDelete = Array.from(this.selectedProducts.keys()).map((originalId) => String(originalId));

      if (productIdsToDelete.length > 0) {
        await window.wishlist.remove(productIdsToDelete, this.currentBoard?.id || window.wishlist.BOARD_CONFIG.DEFAULT_BOARD_ID);
      }

      this.dispatchEvent(
        new CustomEvent('wishlist-toolbar:deleted', {
          detail: {
            deletedProducts: itemsToDelete,
          },
          bubbles: true,
          composed: true,
        }),
      );
    } finally {
      this.loading = false;
      this.loading_delete = false;
      this.busy = false;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  _handleSelectAll() {
    const isSelectingAll = this.selectedProducts.size !== this.selectableProductCount;

    this.dispatchEvent(
      new CustomEvent('wishlist-toolbar:select-all', {
        detail: {
          selectAll: isSelectingAll,
        },
        bubbles: true,
        composed: true,
      }),
    );

    this.requestUpdate();
  }

  render() {
    return html`
      <div class="wishlist_toolbar" ?open=${this.selectedProducts.size > 0} aria-hidden=${this.selectedProducts.size === 0} role="toolbar" aria-label="Wishlist toolbar">
        <button @click=${this._handleDelete} class="btn wishlist_toolbar_delete wishlist_toolbar_delete--desktop" ?disabled=${this.selectedProducts.size === 0 || this.loading || this.busy} aria-label="Delete items from board">
          ${this.loading_delete
            ? html`<bouncing-ellipsis></bouncing-ellipsis>`
            : html`<svg width="33" height="30" viewBox="0 0 33 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M25.9795 9.43945C26.3114 9.45961 26.6265 9.59913 26.8525 9.83398C27.0794 10.0698 27.1985 10.3836 27.1748 10.7061L26.5361 19.3955C26.4184 20.9908 26.3241 22.3068 26.0967 23.3457V23.3467C25.8576 24.432 25.4451 25.3742 24.5762 26.1133C23.709 26.8508 22.6575 27.1593 21.46 27.3008C20.3117 27.4379 18.8662 27.4375 17.1055 27.4375H15.8965C14.3542 27.4375 13.0553 27.4375 11.9854 27.3457L11.541 27.3008C10.3418 27.1592 9.29178 26.8495 8.4248 26.1133C7.55434 25.3741 7.14244 24.4326 6.90332 23.3457C6.6759 22.3068 6.58025 20.9912 6.46387 19.3955L5.8252 10.7061C5.8015 10.3836 5.92057 10.0698 6.14746 9.83398C6.37351 9.59913 6.68863 9.45961 7.02051 9.43945C7.35222 9.41943 7.68234 9.51996 7.93848 9.72363C8.19545 9.92798 8.3591 10.2219 8.38281 10.5439L9.01465 19.1689C9.13893 20.86 9.22633 22.0114 9.41406 22.8711L9.48535 23.1611C9.65769 23.7932 9.8778 24.1251 10.1572 24.3633H10.1562C10.48 24.6382 10.9394 24.8356 11.8672 24.9453C12.8307 25.0594 14.1057 25.0625 15.9678 25.0625H17.0322C18.8958 25.0625 20.1706 25.0594 21.1328 24.9453C22.0603 24.8357 22.5191 24.6381 22.8428 24.3633C23.1621 24.091 23.4044 23.6964 23.5859 22.8711C23.7737 22.0114 23.8611 20.86 23.9854 19.1689L24.6172 10.5439C24.6409 10.2219 24.8046 9.92798 25.0615 9.72363C25.3177 9.51996 25.6478 9.41943 25.9795 9.43945ZM28.1875 6.3125C28.52 6.3125 28.8433 6.43261 29.085 6.65234C29.3273 6.87279 29.4688 7.17704 29.4688 7.5C29.4687 7.82297 29.3274 8.12722 29.085 8.34766C28.8433 8.56739 28.52 8.6875 28.1875 8.6875H4.8125C4.47997 8.6875 4.15675 8.56739 3.91504 8.34766C3.67264 8.12722 3.53127 7.82297 3.53125 7.5C3.53125 7.17704 3.67266 6.87278 3.91504 6.65234C4.15675 6.43261 4.47997 6.3125 4.8125 6.3125H28.1875ZM16.501 1.31152C17.6138 1.31158 18.7009 1.62547 19.6133 2.21191C20.4689 2.76191 21.1332 3.52732 21.5225 4.41699L21.5967 4.59668L21.6338 4.71191C21.7066 4.98221 21.6753 5.26869 21.543 5.52051C21.3923 5.8072 21.1262 6.02068 20.8105 6.12207C20.4952 6.22333 20.1487 6.20679 19.8457 6.0752C19.5426 5.9434 19.3017 5.70445 19.1846 5.40332C18.9915 4.90692 18.6321 4.47299 18.1514 4.16406C17.7305 3.89362 17.2363 3.73144 16.7217 3.69531L16.5 3.6875C15.9065 3.68734 15.3296 3.85499 14.8486 4.16406C14.3679 4.47299 14.0085 4.90692 13.8154 5.40332L13.8135 5.40723C13.6923 5.70311 13.4514 5.93648 13.1504 6.06445C12.8496 6.19227 12.5072 6.20768 12.1953 6.10742C11.8833 6.00709 11.6202 5.79682 11.4688 5.51465C11.3167 5.23118 11.2923 4.90164 11.4033 4.60059L11.4053 4.59668C11.781 3.63059 12.4752 2.79856 13.3877 2.21191C14.3001 1.62536 15.388 1.31152 16.501 1.31152Z" fill="currentColor" stroke="currentColor" stroke-width="0.5" />
                <path d="M12.9365 12.5688C13.2672 12.5388 13.6004 12.6285 13.8633 12.8237C14.1268 13.0195 14.3002 13.3078 14.3359 13.6284V13.6294L15.0234 19.8794L15.0244 19.8862C15.0505 20.2029 14.9386 20.5123 14.7207 20.7485C14.5035 20.9839 14.1986 21.1289 13.873 21.1587C13.5475 21.1884 13.2192 21.1017 12.958 20.9126C12.6962 20.723 12.5202 20.4429 12.4766 20.1284L12.4756 20.1216L11.7881 13.8716C11.7527 13.5503 11.8597 13.2327 12.0781 12.9897C12.2957 12.7478 12.6057 12.5992 12.9365 12.5688ZM20.0605 12.5923C20.386 12.6221 20.691 12.7662 20.9082 13.0015C21.1262 13.2378 21.2381 13.5479 21.2119 13.8647V13.8716L20.9629 13.8442L21.2109 13.8716L20.5234 20.1216V20.1284C20.4798 20.4429 20.3028 20.723 20.041 20.9126C19.7798 21.1016 19.4514 21.1884 19.126 21.1587C18.8005 21.1289 18.4955 20.984 18.2783 20.7485C18.0604 20.5123 17.9486 20.2029 17.9746 19.8862L17.9756 19.8794L18.6631 13.6294L18.6641 13.6216C18.7078 13.3074 18.8839 13.0279 19.1455 12.8384C19.4067 12.6492 19.735 12.5625 20.0605 12.5923Z" fill="currentColor" stroke="currentColor" stroke-width="0.5" />
              </svg> `}
        </button>
        <label for="wishlist_toolbar_select_all" class="wishlist_toolbar_select_all">
          <input type="checkbox" id="wishlist_toolbar_select_all" name="wishlist_toolbar_select_all" .checked=${live(this.selectedProducts.size === this.selectableProductCount)} @change=${this._handleSelectAll} />
          <span class="wishlist_toolbar_select_all_label"
            >Select All
            <span class="wishlist_toolbar_select_all_count"
              >(${this.selectedProducts.size}
              item${this.selectedProducts.size > 1
                ? 's'
                : ''}
              selected)</span
            ></span
          >
        </label>
        <div class="wishlist_toolbar_buttons">
          <button @click=${this._handleDelete} class="btn wishlist_toolbar_delete wishlist_toolbar_delete--mobile" ?disabled=${this.selectedProducts.size === 0 || this.loading || this.busy} aria-label="Delete items from board">
            ${this.loading_delete
              ? html`<bouncing-ellipsis></bouncing-ellipsis>`
              : html`<svg width="33" height="30" viewBox="0 0 33 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M25.9795 9.43945C26.3114 9.45961 26.6265 9.59913 26.8525 9.83398C27.0794 10.0698 27.1985 10.3836 27.1748 10.7061L26.5361 19.3955C26.4184 20.9908 26.3241 22.3068 26.0967 23.3457V23.3467C25.8576 24.432 25.4451 25.3742 24.5762 26.1133C23.709 26.8508 22.6575 27.1593 21.46 27.3008C20.3117 27.4379 18.8662 27.4375 17.1055 27.4375H15.8965C14.3542 27.4375 13.0553 27.4375 11.9854 27.3457L11.541 27.3008C10.3418 27.1592 9.29178 26.8495 8.4248 26.1133C7.55434 25.3741 7.14244 24.4326 6.90332 23.3457C6.6759 22.3068 6.58025 20.9912 6.46387 19.3955L5.8252 10.7061C5.8015 10.3836 5.92057 10.0698 6.14746 9.83398C6.37351 9.59913 6.68863 9.45961 7.02051 9.43945C7.35222 9.41943 7.68234 9.51996 7.93848 9.72363C8.19545 9.92798 8.3591 10.2219 8.38281 10.5439L9.01465 19.1689C9.13893 20.86 9.22633 22.0114 9.41406 22.8711L9.48535 23.1611C9.65769 23.7932 9.8778 24.1251 10.1572 24.3633H10.1562C10.48 24.6382 10.9394 24.8356 11.8672 24.9453C12.8307 25.0594 14.1057 25.0625 15.9678 25.0625H17.0322C18.8958 25.0625 20.1706 25.0594 21.1328 24.9453C22.0603 24.8357 22.5191 24.6381 22.8428 24.3633C23.1621 24.091 23.4044 23.6964 23.5859 22.8711C23.7737 22.0114 23.8611 20.86 23.9854 19.1689L24.6172 10.5439C24.6409 10.2219 24.8046 9.92798 25.0615 9.72363C25.3177 9.51996 25.6478 9.41943 25.9795 9.43945ZM28.1875 6.3125C28.52 6.3125 28.8433 6.43261 29.085 6.65234C29.3273 6.87279 29.4688 7.17704 29.4688 7.5C29.4687 7.82297 29.3274 8.12722 29.085 8.34766C28.8433 8.56739 28.52 8.6875 28.1875 8.6875H4.8125C4.47997 8.6875 4.15675 8.56739 3.91504 8.34766C3.67264 8.12722 3.53127 7.82297 3.53125 7.5C3.53125 7.17704 3.67266 6.87278 3.91504 6.65234C4.15675 6.43261 4.47997 6.3125 4.8125 6.3125H28.1875ZM16.501 1.31152C17.6138 1.31158 18.7009 1.62547 19.6133 2.21191C20.4689 2.76191 21.1332 3.52732 21.5225 4.41699L21.5967 4.59668L21.6338 4.71191C21.7066 4.98221 21.6753 5.26869 21.543 5.52051C21.3923 5.8072 21.1262 6.02068 20.8105 6.12207C20.4952 6.22333 20.1487 6.20679 19.8457 6.0752C19.5426 5.9434 19.3017 5.70445 19.1846 5.40332C18.9915 4.90692 18.6321 4.47299 18.1514 4.16406C17.7305 3.89362 17.2363 3.73144 16.7217 3.69531L16.5 3.6875C15.9065 3.68734 15.3296 3.85499 14.8486 4.16406C14.3679 4.47299 14.0085 4.90692 13.8154 5.40332L13.8135 5.40723C13.6923 5.70311 13.4514 5.93648 13.1504 6.06445C12.8496 6.19227 12.5072 6.20768 12.1953 6.10742C11.8833 6.00709 11.6202 5.79682 11.4688 5.51465C11.3167 5.23118 11.2923 4.90164 11.4033 4.60059L11.4053 4.59668C11.781 3.63059 12.4752 2.79856 13.3877 2.21191C14.3001 1.62536 15.388 1.31152 16.501 1.31152Z" fill="currentColor" stroke="currentColor" stroke-width="0.5" />
                  <path d="M12.9365 12.5688C13.2672 12.5388 13.6004 12.6285 13.8633 12.8237C14.1268 13.0195 14.3002 13.3078 14.3359 13.6284V13.6294L15.0234 19.8794L15.0244 19.8862C15.0505 20.2029 14.9386 20.5123 14.7207 20.7485C14.5035 20.9839 14.1986 21.1289 13.873 21.1587C13.5475 21.1884 13.2192 21.1017 12.958 20.9126C12.6962 20.723 12.5202 20.4429 12.4766 20.1284L12.4756 20.1216L11.7881 13.8716C11.7527 13.5503 11.8597 13.2327 12.0781 12.9897C12.2957 12.7478 12.6057 12.5992 12.9365 12.5688ZM20.0605 12.5923C20.386 12.6221 20.691 12.7662 20.9082 13.0015C21.1262 13.2378 21.2381 13.5479 21.2119 13.8647V13.8716L20.9629 13.8442L21.2109 13.8716L20.5234 20.1216V20.1284C20.4798 20.4429 20.3028 20.723 20.041 20.9126C19.7798 21.1016 19.4514 21.1884 19.126 21.1587C18.8005 21.1289 18.4955 20.984 18.2783 20.7485C18.0604 20.5123 17.9486 20.2029 17.9746 19.8862L17.9756 19.8794L18.6631 13.6294L18.6641 13.6216C18.7078 13.3074 18.8839 13.0279 19.1455 12.8384C19.4067 12.6492 19.735 12.5625 20.0605 12.5923Z" fill="currentColor" stroke="currentColor" stroke-width="0.5" />
                </svg> `}
          </button>
          ${this.currentBoard
            ? html`<button @click=${this._handleMoveToBoard} class="btn btn-secondary" ?disabled=${this.selectedProducts.size === 0 || this.loading || this.busy}>
                ${this.loading_move
                  ? html`<bouncing-ellipsis></bouncing-ellipsis>`
                  : html`${unsafeHTML(window.theme.strings.wishlist.move)}`}
              </button>`
            : nothing}
          <button @click=${this._handleAddToCart} class="btn btn-tertiary" ?disabled=${this.selectedProducts.size === 0 || this.loading || this.busy}>
            ${this.loading_add
              ? html`<bouncing-ellipsis></bouncing-ellipsis>`
              : html`${unsafeHTML(window.theme.strings.wishlist.add_to_bag)}`}
          </button>
        </div>
      </div>
    `;
  }
}

// Register the custom element
window.customElements.get('wishlist-toolbar') || window.customElements.define('wishlist-toolbar', WishlistToolbar);
